<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-back-button [text]="'ZURUECK' | translate"></ion-back-button>
    </ion-buttons>
    <ion-title>{{ 'ABSCHLUSSPROTOKOLL' | translate }}</ion-title>
    <ion-buttons slot="end">
      <ion-menu-button></ion-menu-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content>
  <div class="padding-16 margin-bottom-64">
    <work-ui-form-wizard [formGroupInput]="form" completeButtonLabel="AUFGABE_ABSCHLIESSEN" [completeButtonDisabled]="completeDisabled()"
                         [completeButtonLoadingState]="uploadLoadingState()" (complete)="completeTask()">
      <form [formGroup]="form" autocomplete="off">

        <!-- ═══ Schritt 0: Projekt & Gerüsttyp ════════════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABSCHLUSS_STEP_PROJEKT">
          <div class="flex flex-column gap-16">
            @if (project(); as project) {
              <work-ui-badge
                [text]="project?.status?.name || '-'"
                [variant]="project.statusId | getLabelVariantForStatus"
                size="small" />
              <h1>{{ project.numberQualified }}</h1>

              <!-- Auftraggeber -->
              <div class="work-card padding-16">
                <h2 class="margin-bottom-12">{{ 'AUFTRAGGEBER' | translate }}</h2>
                @if (project.container?.contact; as contact) {
                  <p class="margin-bottom-4"><strong>{{ contact.company }}</strong></p>
                  <p>{{ contact | getFullnameOfContact:true }}</p>
                }
              </div>

              <!-- Objekt Adresse -->
              <div class="work-card padding-16">
                <h2 class="margin-bottom-12">{{ 'OBJEKTADRESSE' | translate }}</h2>
                <p>
                  {{ project.objectStreet }}<br>
                  {{ project.objectZip }} {{ project.objectCity }} ({{ project.objectCanton }})
                </p>
              </div>
            }

            <!-- Gerüsttyp -->
            <div class="work-card padding-16">
              <h2 class="margin-bottom-12">{{ 'GERUESTTYP' | translate }}</h2>
              <div class="flex flex-column gap-12">
                @for (option of geruestTypOptions; track option.key) {
                  <work-ui-checkbox
                    [label]="option.label"
                    [checked]="form.controls.geruestTyp.value[option.key]"
                    [noMargin]="true"
                    (checkedChange)="toggleGeruestTyp(option.key)" />
                }
                @if (form.controls.geruestTyp.invalid && form.controls.geruestTyp.touched) {
                  <p class="error-text">{{ 'BITTE_MINDESTENS_EINE_OPTION_AUSWAEHLEN' | translate }}</p>
                }
              </div>
            </div>

            <!-- Lastklasse -->
            <div class="work-card padding-16">
              <h2 class="margin-bottom-12">{{ 'LASTKLASSE' | translate }}</h2>
              <div class="flex flex-column gap-12">
                <work-ui-input-field
                  formControlName="lastklasse"
                  label="LASTKLASSE_IN_KN_M2"
                  type="number"
                  [required]="true" />
                <div class="flex flex-row gap-8">
                  <work-ui-button text="2 kN/m²" size="small" variant="secondary" (buttonClick)="setLastklasse(2)" />
                  <work-ui-button text="3 kN/m²" size="small" variant="secondary" (buttonClick)="setLastklasse(3)" />
                </div>
              </div>
            </div>
          </div>
        </ng-template>

        <!-- ═══ Schritt 1: Tragfähigkeit & Zugänge ════════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABNAHME_STEP_TRAGFAEHIGKEIT_ZUGAENGE">
          <div class="flex flex-column gap-16">
            @for (question of getQuestionsForStep(1); track question.key) {
              <div class="work-card">
                <p><strong>{{ question.question | translate }}</strong></p>
                @if (question.notes?.length) {
                  <ul class="abnahme-notes margin-top-8">
                    @for (note of question.notes!; track $index) {
                      <li><small>{{ note | translate }}</small></li>
                    }
                  </ul>
                }
                @if (question.images) {
                  <div class="flex flex-column gap-8 margin-top-8">
                    @for (_ of asArray(question.images); track $index) {
                      <div class="abnahme-bild-platzhalter">
                        <img src="assets/images/abschlussprotokoll/{{question.key}}_{{$index+1}}.png" [alt]="'BILD' | translate">
                      </div>
                    }
                  </div>
                }
                <div class="abnahme-antwort-gruppe margin-top-12">
                  @for (answer of question.answers; track answer) {
                    <button
                      type="button"
                      class="abnahme-antwort-btn"
                      [ngClass]="{ 'abnahme-antwort-btn--selected': getAnswer(question.key) === answer }"
                      (click)="setAnswer(question.key, answer)">
                      {{ getAnswerLabel(answer) | translate }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>

        <!-- ═══ Schritt 2: Unterlage & Verankerungen ══════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABNAHME_STEP_UNTERLAGE_VERANKERUNGEN">
          <div class="flex flex-column gap-16">
            @for (question of getQuestionsForStep(2); track question.key) {
              <div class="work-card">
                <p><strong>{{ question.question | translate }}</strong></p>
                @if (question.notes?.length) {
                  <ul class="abnahme-notes margin-top-8">
                    @for (note of question.notes!; track $index) {
                      <li><small>{{ note | translate }}</small></li>
                    }
                  </ul>
                }
                @if (question.images) {
                  <div class="flex flex-column gap-8 margin-top-8">
                    @for (_ of asArray(question.images); track $index) {
                      <div class="abnahme-bild-platzhalter">
                        <img src="assets/images/abschlussprotokoll/{{question.key}}_{{$index+1}}.png" [alt]="'BILD' | translate">
                      </div>
                    }
                  </div>
                }
                <div class="abnahme-antwort-gruppe margin-top-12">
                  @for (answer of question.answers; track answer) {
                    <button
                      type="button"
                      class="abnahme-antwort-btn"
                      [ngClass]="{ 'abnahme-antwort-btn--selected': getAnswer(question.key) === answer }"
                      (click)="setAnswer(question.key, answer)">
                      {{ getAnswerLabel(answer) | translate }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>

        <!-- ═══ Schritt 3: Beläge & Seitenschutz ══════════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABNAHME_STEP_BELAEGE_SEITENSCHUTZ">
          <div class="flex flex-column gap-16">
            @for (question of getQuestionsForStep(3); track question.key) {
              <div class="work-card">
                <p><strong>{{ question.question | translate }}</strong></p>
                @if (question.notes?.length) {
                  <ul class="abnahme-notes margin-top-8">
                    @for (note of question.notes!; track $index) {
                      <li><small>{{ note | translate }}</small></li>
                    }
                  </ul>
                }
                <div class="abnahme-antwort-gruppe margin-top-12">
                  @for (answer of question.answers; track answer) {
                    <button
                      type="button"
                      class="abnahme-antwort-btn"
                      [ngClass]="{ 'abnahme-antwort-btn--selected': getAnswer(question.key) === answer }"
                      (click)="setAnswer(question.key, answer)">
                      {{ getAnswerLabel(answer) | translate }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>

        <!-- ═══ Schritt 4: Spenglergang ════════════════════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABNAHME_STEP_SPENGLERGANG">
          <div class="flex flex-column gap-16">
            @for (question of getQuestionsForStep(4); track question.key) {
              <div class="work-card">
                <p><strong>{{ question.question | translate }}</strong></p>
                @if (question.notes?.length) {
                  <ul class="abnahme-notes margin-top-8">
                    @for (note of question.notes!; track $index) {
                      <li><small>{{ note | translate }}</small></li>
                    }
                  </ul>
                }
                @if (question.images) {
                  <div class="flex flex-column gap-8 margin-top-8">
                    @for (_ of asArray(question.images); track $index) {
                      <div class="abnahme-bild-platzhalter">
                        <img src="assets/images/abschlussprotokoll/{{question.key}}_{{$index+1}}.png" [alt]="'BILD' | translate">
                      </div>
                    }
                  </div>
                }
                <div class="abnahme-antwort-gruppe margin-top-12">
                  @for (answer of question.answers; track answer) {
                    <button
                      type="button"
                      class="abnahme-antwort-btn"
                      [ngClass]="{ 'abnahme-antwort-btn--selected': getAnswer(question.key) === answer }"
                      (click)="setAnswer(question.key, answer)">
                      {{ getAnswerLabel(answer) | translate }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>

        <!-- ═══ Schritt 5: Dachränder ══════════════════════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABNAHME_STEP_DACHRANDER">
          <div class="flex flex-column gap-16">
            @for (question of getQuestionsForStep(5); track question.key) {
              <div class="work-card">
                <p><strong>{{ question.question | translate }}</strong></p>
                @if (question.images) {
                  <div class="flex flex-column gap-8 margin-top-8">
                    @for (_ of asArray(question.images); track $index) {
                      <div class="abnahme-bild-platzhalter">
                        <img src="assets/images/abschlussprotokoll/{{question.key}}_{{$index+1}}.png" [alt]="'BILD' | translate">
                      </div>
                    }
                  </div>
                }
                <div class="abnahme-antwort-gruppe margin-top-12">
                  @for (answer of question.answers; track answer) {
                    <button
                      type="button"
                      class="abnahme-antwort-btn"
                      [ngClass]="{ 'abnahme-antwort-btn--selected': getAnswer(question.key) === answer }"
                      (click)="setAnswer(question.key, answer)">
                      {{ getAnswerLabel(answer) | translate }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>

        <!-- ═══ Schritt 6: Verkehrssicherheit ═════════════════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABNAHME_STEP_SICHERHEIT">
          <div class="flex flex-column gap-16">
            @for (question of getQuestionsForStep(6); track question.key) {
              <div class="work-card">
                <p><strong>{{ question.question | translate }}</strong></p>
                <div class="abnahme-antwort-gruppe margin-top-12">
                  @for (answer of question.answers; track answer) {
                    <button
                      type="button"
                      class="abnahme-antwort-btn"
                      [ngClass]="{ 'abnahme-antwort-btn--selected': getAnswer(question.key) === answer }"
                      (click)="setAnswer(question.key, answer)">
                      {{ getAnswerLabel(answer) | translate }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>

        <!-- ═══ Schritt 7: Anleitung, Fotos & Unterschrift ════════════════════ -->
        <ng-template workUiFormWizardStep stepTitle="ABSCHLUSS_STEP_ABSCHLUSS" (stepActive)="onSignatureStepActive($event)">
          <div class="flex flex-column gap-16">

            <!-- Anleitung -->
            <div class="work-card padding-16">
              <h2 class="margin-bottom-12">{{ 'ANLEITUNG' | translate }}</h2>
              <div class="flex flex-column gap-12">
                <work-ui-checkbox
                  formControlName="anleitungAmGeruest"
                  label="ANLEITUNG_AM_GERUEST"
                  [noMargin]="true" />
                <work-ui-checkbox
                  formControlName="anleitungAmErsteller"
                  label="ANLEITUNG_AM_ERSTELLER"
                  [noMargin]="true" />
                <work-ui-input-field
                  formControlName="anleitungFreitext"
                  label="FREITEXT"
                  type="text" />
              </div>
            </div>

            <!-- Fotos -->
            <div class="work-card padding-16">
              <h2 class="margin-bottom-12">{{ 'FOTOS' | translate }}</h2>
              <work-ui-gallery
                [galleryDocuments]="documents()"
                [projectId]="project()?.id ?? null"
                [documentTypeId]="2"
                [documentsubtypeId]="11"
                [selectModeEnabled]="false"
                (newDocumentsUploaded)="onDocumentsUploaded($event)"
                (documentDeleted)="onDocumentDeleted($event)"
                (uploadingStateChanged)="isUploading.set($event)" />
            </div>

            <!-- Unterschrift -->
            <div class="work-card flex flex-column gap-16">
              <div class="flex flex-row space-between vertical-center">
                <h2>{{ 'UNTERSCHRIFT' | translate }}</h2>
                <work-ui-button
                  text="LOESCHEN"
                  size="small"
                  variant="tertiary"
                  iconName="delete"
                  (buttonClick)="clearSignature()" />
              </div>
              <div class="signature-container">
                <canvas
                  #signatureCanvas
                  class="signature-canvas"
                  (touchstart)="startDrawing($event)"
                  (touchmove)="draw($event)"
                  (touchend)="stopDrawing()"
                  (mousedown)="startDrawing($event)"
                  (mousemove)="draw($event)"
                  (mouseup)="stopDrawing()"
                  (mouseleave)="stopDrawing()">
                </canvas>
              </div>
            </div>

            <!-- E-Mail Hinweis -->
            <div class="work-card padding-16">
              <h2 class="margin-bottom-12">{{ 'HINWEIS' | translate }}</h2>
              <p class="margin-bottom-8">{{ 'ABNAHMEPROTOKOLL_EMAIL_HINWEIS' | translate }}</p>
              <div class="flex flex-column gap-12">
                @if (project(); as project) {
                  @let mails = project?.externalProjectManagerContact?.mails;
                  @if (mails && mails.length > 0) {
                    @for (mail of mails; track mail.id) {
                      <p>{{ mail.value }}</p>
                    }
                  }
                  @let mailsOfClient = project?.clientContact?.mails;
                  @if (mailsOfClient && mailsOfClient.length > 0) {
                    @for (mail of mailsOfClient; track mail.id) {
                      <p class="margin-bottom-8">{{ mail.value }}</p>
                    }
                  }
                }
              </div>
            </div>

          </div>
        </ng-template>

      </form>
    </work-ui-form-wizard>
  </div>
</ion-content>
